import type { Person } from './types';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export interface Birthday {
  /** 1–12 */
  month: number;
  day: number;
  /** Null when the text has no year ("14 March"). */
  year: number | null;
}

/** Which way round "04/05/1990" is read when it could be either: day first (4 May) or month first (5 April). */
export type DateOrder = 'dmy' | 'mdy';

const SLASH = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/;

/** Both numbers could be the day (or month), so the order decides the meaning. */
export function isAmbiguousSlashDate(text: string | null | undefined): boolean {
  const m = SLASH.exec((text ?? '').trim());
  return Boolean(m && Number(m[1]) <= 12 && Number(m[2]) <= 12 && m[1] !== m[2]);
}

/**
 * Work out whether people in this tree write dates day-first or month-first, from the dates that can only
 * be read one way ("24/04/1971" is day first, "04/24/1971" is month first). Day first when there's no evidence.
 */
export function detectDateOrder(texts: Iterable<string | null | undefined>): DateOrder {
  let dmy = 0;
  let mdy = 0;
  for (const t of texts) {
    const m = SLASH.exec((t ?? '').trim());
    if (!m) continue;
    if (Number(m[1]) > 12 && Number(m[2]) <= 12) dmy++;
    else if (Number(m[2]) > 12 && Number(m[1]) <= 12) mdy++;
  }
  return mdy > dmy ? 'mdy' : 'dmy';
}

/**
 * Month and day from the free-text birth date. Understands "14 Mar 1952", "March 14, 1952", "1952-03-14",
 * "14/03/1952" (also with - or .; `order` settles ones like "04/05/1952") and "14 March" (no year). A bare year, or anything else we can't read confidently, gives null.
 */
export function parseBirthday(born: string | null | undefined, order: DateOrder = 'dmy'): Birthday | null {
  const text = (born ?? '').trim().toLowerCase();
  if (!text) return null;
  const valid = (month: number, day: number, year: number | null): Birthday | null =>
    month >= 1 && month <= 12 && day >= 1 && day <= DAYS_IN_MONTH[month - 1]! ? { month, day, year } : null;

  const slash = SLASH.exec(text);
  if (slash) {
    const a = Number(slash[1]);
    const b = Number(slash[2]);
    const dayFirst = a > 12 ? true : b > 12 ? false : order === 'dmy';
    return dayFirst ? valid(b, a, Number(slash[3])) : valid(a, b, Number(slash[3]));
  }

  const iso = /(?<!\d)(\d{4})-(\d{1,2})-(\d{1,2})(?!\d)/.exec(text);
  if (iso) return valid(Number(iso[2]), Number(iso[3]), Number(iso[1]));

  const m = MONTHS.findIndex((x) => new RegExp(`(?<![a-z])${x}[a-z]*`).test(text));
  if (m < 0) return null;
  const rest = text.replace(new RegExp(`(?<![a-z])${MONTHS[m]}[a-z]*\\.?`), ' ');
  const yearMatch = /(?<!\d)(\d{4})(?!\d)/.exec(rest);
  const year = yearMatch ? Number(yearMatch[1]) : null;
  const dayMatch = /(?<!\d)(\d{1,2})(?!\d)(?:st|nd|rd|th)?/.exec(rest.replace(/(?<!\d)\d{4}(?!\d)/, ' '));
  if (!dayMatch) return null;
  return valid(m + 1, Number(dayMatch[1]), year && year >= 1000 && year <= 2999 ? year : null);
}

/** Birthday of a person, taking the year from `bornYear` when the text lacks one. */
export function birthdayOf(p: Pick<Person, 'born' | 'bornYear'>, order: DateOrder = 'dmy'): Birthday | null {
  const b = parseBirthday(p.born, order);
  return b ? { ...b, year: b.year ?? p.bornYear } : null;
}

const ms = (y: number, m: number, d: number) => Date.UTC(y, m - 1, d);

/** Days from `today` until the next occurrence of the birthday (0 = today). 29 Feb is kept on 28 Feb in non-leap years. */
export function daysUntil(b: Pick<Birthday, 'month' | 'day'>, today: Date): number {
  const y = today.getFullYear();
  const from = ms(y, today.getMonth() + 1, today.getDate());
  const at = (yr: number) => {
    const leap = (yr % 4 === 0 && yr % 100 !== 0) || yr % 400 === 0;
    return ms(yr, b.month, b.month === 2 && b.day === 29 && !leap ? 28 : b.day);
  };
  let t = at(y);
  if (t < from) t = at(y + 1);
  return Math.round((t - from) / 86_400_000);
}

/** True when this year's birthday has already gone by (today itself doesn't count). */
function passedThisYear(b: Pick<Birthday, 'month' | 'day'>, today: Date): boolean {
  const m = today.getMonth() + 1;
  return b.month < m || (b.month === m && b.day < today.getDate());
}

/** Age they turn on their next birthday (today's, if it is today); null if the birth year is unknown. */
export function turningAge(b: Birthday, today: Date): number | null {
  if (b.year === null) return null;
  return today.getFullYear() + (passedThisYear(b, today) ? 1 : 0) - b.year;
}

/** Whole years lived as of `today`; null without a birth year. */
export function ageOn(b: Birthday | { year: number | null; month?: number; day?: number }, today: Date): number | null {
  if (b.year === null) return null;
  if (b.month === undefined || b.day === undefined) return today.getFullYear() - b.year;
  const notYet = !passedThisYear({ month: b.month, day: b.day }, today) && daysUntil({ month: b.month, day: b.day }, today) > 0;
  return today.getFullYear() - b.year - (notYet ? 1 : 0);
}

const pad = (n: number) => String(n).padStart(2, '0');
const SHORT_MONTHS: Record<'en' | 'es', string[]> = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'],
};

/** How a stored date reads back in the date picker: a full date, just a year, empty, or free text we can't represent. */
export type DateValue =
  | { kind: 'empty' }
  | { kind: 'full'; iso: string }
  | { kind: 'year'; year: string }
  | { kind: 'legacy'; text: string };

/**
 * Interpret a stored date. Pickable values are full dates ("1952-03-14", or older free text such as
 * "14 Mar 1952") and bare years. Anything else ("about 1950", "March 1952") is kept verbatim as 'legacy'
 * so opening and saving a profile never loses information.
 */
export function readDate(text: string | null | undefined, order: DateOrder = 'dmy'): DateValue {
  const t = (text ?? '').trim();
  if (!t) return { kind: 'empty' };
  if (/^\d{4}$/.test(t)) return { kind: 'year', year: t };
  const b = parseBirthday(t, order);
  const words = t.toLowerCase().match(/[a-z]+|\d+/g) ?? [];
  const clean = words.every((w) => /^\d+$/.test(w) || /^(st|nd|rd|th)$/.test(w) || MONTHS.some((m) => w.startsWith(m)));
  if (b && b.year !== null && b.year >= 1000 && clean) return { kind: 'full', iso: `${b.year}-${pad(b.month)}-${pad(b.day)}` };
  return { kind: 'legacy', text: t };
}

/** "1952-03-14" → "14 Mar 1952" (Spanish: "14 mar 1952"); anything else is shown as it was typed. */
export function formatDate(text: string | null | undefined, order: DateOrder = 'dmy', locale: 'en' | 'es' = 'en'): string {
  const v = readDate(text, order);
  if (v.kind === 'full') {
    const [y, m, d] = v.iso.split('-').map(Number);
    return `${d} ${SHORT_MONTHS[locale][m! - 1]} ${y}`;
  }
  return (text ?? '').trim();
}
